import React from 'react';
import { ArrowLeft } from 'lucide-react';
import '../../styles/realsignal-theme.css';

interface TermsOfServicePageProps {
    onBack: () => void;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    border: '#1C2E42',
};

export const TermsOfServicePage: React.FC<TermsOfServicePageProps> = ({ onBack }) => {
    return (
        <div className="min-h-screen overflow-auto" style={{ background: colors.background }}>
            <div className="max-w-3xl mx-auto px-6 py-12" style={{ maxWidth: '800px' }}>
                <button
                    onClick={onBack}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: colors.textSecondary,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        marginBottom: '2rem',
                    }}
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Sign In</span>
                </button>

                <h1
                    style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: colors.textPrimary,
                        marginBottom: '0.5rem',
                    }}
                >
                    Terms of Service
                </h1>
                <p
                    style={{
                        color: colors.textMuted,
                        marginBottom: '2rem',
                    }}
                >
                    Last updated: January 2026
                </p>

                <div
                    style={{
                        background: colors.backgroundLight,
                        borderRadius: '1rem',
                        padding: '2rem',
                        color: colors.textSecondary,
                        lineHeight: 1.7,
                    }}
                >
                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            1. Acceptance of Terms
                        </h2>
                        <p>
                            By accessing or using RealSignal ("the Service"), you agree to be bound
                            by these Terms of Service. If you do not agree to these terms, please do
                            not use the Service.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            2. Description of Service
                        </h2>
                        <p>
                            RealSignal provides real estate distress intelligence services,
                            including property data analysis, foreclosure tracking, and market
                            insights for Harris County, Texas. The Service is intended for
                            professional use by real estate investors, agents, and related
                            professionals.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            3. Beta Access
                        </h2>
                        <p>
                            During the beta period, access to RealSignal is limited to users with
                            valid beta codes. Beta codes are single-use and non-transferable. Beta
                            users acknowledge that the Service may contain bugs, errors, or
                            incomplete features.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            4. User Accounts
                        </h2>
                        <p>
                            You are responsible for maintaining the confidentiality of your account
                            credentials and for all activities that occur under your account. You
                            agree to notify us immediately of any unauthorized use of your account.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            5. Acceptable Use
                        </h2>
                        <p>
                            You agree not to use the Service for any unlawful purpose or in any way
                            that could damage, disable, or impair the Service. You may not attempt
                            to gain unauthorized access to any portion of the Service or any systems
                            connected to the Service.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            6. Data Accuracy
                        </h2>
                        <p>
                            While we strive to provide accurate and up-to-date information,
                            RealSignal does not guarantee the accuracy, completeness, or timeliness
                            of any data provided through the Service. Users should independently
                            verify all information before making investment decisions.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            7. Subscription and Billing
                        </h2>
                        <p>
                            Paid subscriptions are billed on a recurring basis. You may cancel your
                            subscription at any time through your account settings. Refunds are
                            provided in accordance with our refund policy.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            8. Limitation of Liability
                        </h2>
                        <p>
                            To the maximum extent permitted by law, RealSignal and its affiliates
                            shall not be liable for any indirect, incidental, special,
                            consequential, or punitive damages arising from your use of the Service.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            9. Changes to Terms
                        </h2>
                        <p>
                            We reserve the right to modify these Terms at any time. We will notify
                            users of any material changes via email or through the Service.
                            Continued use of the Service after such modifications constitutes
                            acceptance of the updated Terms.
                        </p>
                    </section>

                    <section>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            10. Contact
                        </h2>
                        <p>
                            For questions about these Terms, please contact us at
                            support@realsignal.app.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TermsOfServicePage;
